/**
 * AvailableTemplatesPanel - List and Add Available Category Templates
 * 
 * Displays templates that the user doesn't have and allows adding them.
 * 
 * @validates Requirements 6.1, 6.2, 6.3 - Template availability and re-addition
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableTemplatesStore } from '../store/availableTemplatesStore';
import { CategoryTemplate } from '@budgira/shared';

// Icons
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

interface AvailableTemplatesPanelProps {
  onTemplateAdded?: () => void;
  canEdit?: boolean;
}

interface TemplateCardProps {
  template: CategoryTemplate;
  onAdd: (templateId: string) => void;
  isAdding: boolean;
  canEdit: boolean;
}

const TemplateCard = ({ template, onAdd, isAdding, canEdit }: TemplateCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-colors">
      <div className="flex items-center gap-3">
        {/* Logo/Icon */}
        <span className="text-lg flex-shrink-0">
          {template.logoUrl || <FolderIcon />}
        </span>

        {/* Name */}
        <span className="font-medium text-slate-700 dark:text-slate-200">
          {template.name}
        </span>

        {/* Type badge */}
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          template.baseType === 'credit' 
            ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' 
            : 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300'
        }`}>
          {template.baseType === 'credit' ? t('categories.modal.credit', 'Crédit') : t('categories.modal.debit', 'Débit')}
        </span>
      </div>

      {/* Add button - only show if user can edit */}
      {canEdit && (
        <button
          onClick={() => onAdd(template.id)}
          disabled={isAdding}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <PlusIcon />
          <span>{t('availableTemplates.add', 'Ajouter')}</span>
        </button>
      )}
    </div>
  );
};

export default function AvailableTemplatesPanel({ onTemplateAdded, canEdit = true }: AvailableTemplatesPanelProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [addingTemplateId, setAddingTemplateId] = useState<string | null>(null);

  const { 
    templates, 
    isLoading, 
    error, 
    fetchAvailableTemplates, 
    addTemplate,
    clearError 
  } = useAvailableTemplatesStore();

  useEffect(() => {
    fetchAvailableTemplates();
  }, [fetchAvailableTemplates]);

  const handleAddTemplate = async (templateId: string) => {
    setAddingTemplateId(templateId);
    try {
      const result = await addTemplate(templateId);
      if (result) {
        onTemplateAdded?.();
      }
    } finally {
      setAddingTemplateId(null);
    }
  };

  // Don't render if no templates available and not loading
  if (!isLoading && templates.length === 0 && !error) {
    return null;
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-t-xl transition-colors"
      >
        <h2 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          {isExpanded ? (
            <ChevronDownIcon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          )}
          {t('availableTemplates.title', 'Templates disponibles')}
          {templates.length > 0 && (
            <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
              ({templates.length})
            </span>
          )}
        </h2>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-4 pb-4">
          {/* Error display */}
          {error && (
            <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-3 flex items-center justify-between mb-4">
              <p className="text-rose-700 dark:text-rose-300 text-sm">{error}</p>
              <button
                onClick={clearError}
                className="text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300"
              >
                ✕
              </button>
            </div>
          )}

          {/* Loading state */}
          {isLoading && templates.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-3 border-violet-200 dark:border-violet-800 border-t-violet-600 dark:border-t-violet-400 rounded-full animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && templates.length === 0 && !error && (
            <p className="text-slate-500 dark:text-slate-400 text-center py-4">
              {t('availableTemplates.empty', 'Tous les templates sont déjà ajoutés')}
            </p>
          )}

          {/* Templates list */}
          {templates.length > 0 && (
            <div className="space-y-2">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onAdd={handleAddTemplate}
                  isAdding={addingTemplateId === template.id}
                  canEdit={canEdit}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
